import { DirectusRepository, PocketBaseRepository, type FintracksRepository } from '@fintracks/directus-sdk';
import { FINTRACKS_FILES_FOLDER_ID, type CapturedFinanceData } from '@fintracks/contracts';
import { demoRepository } from './localRepository';

export type AppMode = 'demo' | 'directus' | 'pocketbase';
export { FINTRACKS_FILES_FOLDER_ID } from '@fintracks/contracts';

type CapturedFinanceReader = {
  getCapturedFinanceData?: (clientId: string) => Promise<CapturedFinanceData | null>;
};

/**
 * App-facing repository surface. The captured-finance method is deliberately
 * additive: an older provider remains usable and reports no optional extension.
 */
export type AppRepository = FintracksRepository & {
  getCapturedFinanceData?: (clientId: string) => Promise<CapturedFinanceData | null>;
};

function withCapturedFinanceRead(repository: FintracksRepository): AppRepository {
  // Capture the SDK method before adding the app wrapper, otherwise the wrapper
  // would call itself after Object.assign. There is no alternate backend here.
  const sdkReader = (repository as FintracksRepository & CapturedFinanceReader).getCapturedFinanceData;
  return Object.assign(repository, {
    getCapturedFinanceData(clientId: string): Promise<CapturedFinanceData | null> {
      if (typeof sdkReader !== 'function') return Promise.resolve(null);
      return sdkReader.call(repository, clientId);
    },
  });
}

export function createRepository(
  env: Pick<ImportMetaEnv, 'VITE_APP_MODE' | 'VITE_DIRECTUS_URL' | 'VITE_FILES_NAMESPACE_ID'> & { VITE_DIRECTUS_FILES_FOLDER_ID?: string } = import.meta.env,
): AppRepository {
  const mode = env.VITE_APP_MODE ?? (env === import.meta.env && import.meta.env.MODE === 'test' ? 'demo' : undefined);
  if (mode !== 'demo' && mode !== 'directus' && mode !== 'pocketbase') throw new Error('VITE_APP_MODE deve ser explicitamente demo, directus ou pocketbase.');
  if (mode === 'demo') return withCapturedFinanceRead(demoRepository);
  const directusUrl = env.VITE_DIRECTUS_URL?.trim() ?? '';
  if (mode === 'pocketbase') {
    if (directusUrl) throw new Error('Modo PocketBase não aceita VITE_DIRECTUS_URL; deixe a URL Directus vazia.');
    const filesNamespaceId = env.VITE_FILES_NAMESPACE_ID?.trim();
    if (!filesNamespaceId || filesNamespaceId !== FINTRACKS_FILES_FOLDER_ID) throw new Error('Modo PocketBase exige VITE_FILES_NAMESPACE_ID com o UUID canônico do namespace lógico Fintracks.');
    return withCapturedFinanceRead(new PocketBaseRepository({
      baseUrl: '/api/pocketbase-wire',
      edgeBaseUrl: '/api',
      fileGatewayBaseUrl: '/api',
      contributionGatewayBaseUrl: '/api',
      directusOnly: true,
      filesNamespaceId,
      casWrites: true,
    }));
  }
  const filesFolderId = env.VITE_DIRECTUS_FILES_FOLDER_ID?.trim();
  if (!filesFolderId || filesFolderId !== FINTRACKS_FILES_FOLDER_ID) throw new Error('Storage privado Fintracks não configurado: informe o UUID canônico em VITE_DIRECTUS_FILES_FOLDER_ID.');
  if (directusUrl && !/^\/(?:directus)\/?$/.test(directusUrl)) throw new Error('VITE_DIRECTUS_URL deve apontar somente para o proxy same-origin /directus.');
  return withCapturedFinanceRead(new DirectusRepository({
    baseUrl: directusUrl || '/directus',
    fileGatewayBaseUrl: '/api',
    contributionGatewayBaseUrl: '/api',
    directusOnly: true,
    filesFolderId,
    casWrites: true,
  }));
}

export const repository = createRepository();
export const appMode = repository.mode;
